import { notify } from '@billy/design-system'
import { Button } from '@shinetools/lumen-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router'

import { useUpdateContact } from '@features/contacts/hooks/useUpdateContact'
import { Contact } from '@features/contacts/types/contact'
import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'

import { NotificationKeys } from '../../../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../../../enums/queryKeys'
import { useSegment } from '../../../../../../../../hooks'
import { useOrganizationViewUrl } from '../../../../../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../../../../../utils/routing/reactRoute'
import { HeaderSupplierDetailMenu } from './elements/HeaderSupplierDetailMenu'

interface HeaderSupplierDetailActionsProps {
  contact: Contact | null
  onEditButtonClick: () => void
}

export const HeaderSupplierDetailActions = ({ contact, onEditButtonClick }: HeaderSupplierDetailActionsProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { track } = useSegment()
  const { url: newBillUrl } = useOrganizationViewUrl(
    reactRoute.organization.bills.getNewBillFromContactRoute(contact?.id),
  )

  const { isProcessing: isUnarchiving, update: updateContact } = useUpdateContact({
    onError: () => {
      notify({
        id: NotificationKeys.UpdateContact,
        message: t('suppliers.detail.unarchive.notification.error'),
        variant: 'error',
      })
    },
    onSuccess: async () => {
      if (!contact) {
        return
      }

      await Promise.all([
        queryClient.invalidateQueries([QueryKeys.Contact, contact.id]),
        queryClient.invalidateQueries([QueryKeys.Contacts]),
        queryClient.invalidateQueries([QueryKeys.ContactsAndContactPersons]),
      ])
      notify({
        id: NotificationKeys.UpdateContact,
        message: t('suppliers.detail.unarchive.notification.success'),
        variant: 'success',
      })
    },
  })

  const handleAddPurchaseButtonClick = useCallback(() => {
    track(ExpensesTrackingEvents.CreatePurchaseClicked, {
      source: 'Supplier',
    })

    if (newBillUrl) {
      navigate(newBillUrl)
    }
  }, [navigate, newBillUrl, track])

  const handleUnarchiveButtonClick = useCallback(() => {
    if (contact) {
      updateContact({ id: contact.id, isArchived: false })
    }
  }, [contact, updateContact])

  if (contact?.isArchived) {
    return (
      <Button
        label={t('suppliers.detail.unarchive')}
        loading={isUnarchiving}
        onClick={handleUnarchiveButtonClick}
        variant="tonal"
      />
    )
  }

  return (
    <>
      {contact ? <HeaderSupplierDetailMenu contact={contact} /> : null}
      <Button label={t('edit')} onClick={onEditButtonClick} variant="tonal" />
      <Button
        color="primary"
        disabled={!contact || !newBillUrl}
        label={t('create_bill')}
        onClick={handleAddPurchaseButtonClick}
        variant="filled"
      />
    </>
  )
}
